import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const faqGroups = [
  {
    group: "Basics",
    items: [
      { q: "What is AVault?", a: "AVault is an offline-first password and secrets manager. It stores logins, cards, identities, notes and documents in an encrypted file on your device." },
      { q: "Is AVault really offline?", a: "Yes. The vault file lives on your device. We don't host servers that hold your data. Optional sync moves only the encrypted bundle." },
      { q: "Do I need an account?", a: "No. Free tier requires no sign-up. Pro is licensed against your email but the vault itself never touches our servers." },
      { q: "What platforms are supported?", a: "macOS 12+, Windows 10/11, Linux (AppImage/deb/rpm), iOS 16+, Android 10+." },
    ],
  },
  {
    group: "Security",
    items: [
      { q: "How is data encrypted?", a: "Each item is sealed with AES-256-GCM. Keys are derived from your master password using Argon2id (memory-hard) — PBKDF2-SHA-512 fallback for legacy hardware." },
      { q: "Can AVault recover my master password?", a: "No. We don't store, transmit, or escrow it. Use the encrypted backup feature and keep a copy somewhere safe." },
      { q: "What happens if my device is stolen?", a: "Your vault stays sealed. Without your master password (and biometric, if enabled) it's indistinguishable from random bytes." },
      { q: "Does biometric unlock weaken security?", a: "No — biometrics only release a locally-stored key fragment after the OS authenticates you. The master password is still required for sensitive operations like backup export." },
    ],
  },
  {
    group: "Vaults & sync",
    items: [
      { q: "Can I have more than one vault?", a: "Yes — Pro gives unlimited vaults. Each has its own master password and encryption keys." },
      { q: "What is a decoy vault?", a: "A separate vault that opens with an alternate master password — useful for plausible deniability under coercion." },
      { q: "How does sync work?", a: "AVault never operates a sync server. You can store the encrypted .avault file in any cloud (iCloud, Drive, Dropbox) — they only ever see ciphertext." },
      { q: "Can I export my data?", a: "Yes. Export as encrypted .avault bundle, or as decrypted JSON if you choose. Decrypted exports are clearly labelled and audited in the activity log." },
    ],
  },
  {
    group: "Pricing",
    items: [
      { q: "Is the free tier actually free?", a: "Yes. No trial, no credit card. One vault, up to 25 items, forever." },
      { q: "When should I upgrade?", a: "When you want unlimited vaults & items, the decoy vault, biometric unlock, or encrypted backup/restore." },
      { q: "Can I cancel anytime?", a: "Yes. Pro features simply unlist on your next renewal — your data stays exactly where it is." },
    ],
  },
];

export default function FAQ() {
  return (
    <div>
      <section className="py-20 lg:py-28" data-testid="faq-hero">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="label-eyebrow mb-5">FAQ</p>
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95]">
            Questions, answered.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
            Can&apos;t find what you&apos;re looking for? Reach us at hello@avault.app.
          </p>
        </div>
      </section>

      <section className="pb-24" data-testid="faq-groups">
        <div className="max-w-3xl mx-auto px-6 space-y-12">
          {faqGroups.map((g) => (
            <div key={g.group} data-testid={`faq-group-${g.group.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="label-eyebrow mb-4">{g.group}</p>
              <Accordion type="single" collapsible>
                {g.items.map((it, i) => (
                  <AccordionItem
                    key={i}
                    value={`${g.group}-${i}`}
                    className="border-b border-border"
                    data-testid={`faq-item-${g.group.toLowerCase()}-${i}`}
                  >
                    <AccordionTrigger className="text-left text-base font-semibold py-5 hover:no-underline">
                      {it.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed pb-5">
                      {it.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
